import {
  ComponentPropsWithRef,
  forwardRef,
  memo,
  ReactNode,
  RefObject,
} from 'react';
// eslint-disable-next-line import/extensions
import { TooltipVariantProps } from 'node_modules/@chakra-ui/react/dist/types/styled-system/generated/recipes.gen';
import {
  Portal,
  Tooltip as ChakraTooltip,
  useSlotRecipe,
} from '@chakra-ui/react';

import { Nullable } from '../../../types';

interface TooltipProps extends ChakraTooltip.RootProps {
  showArrow?: boolean;
  portalled?: boolean;
  portalRef?: RefObject<Nullable<HTMLElement>>;
  content: ReactNode;
  contentProps?: ChakraTooltip.ContentProps;
  disabled?: boolean;
  size?: TooltipVariantProps['size'];
  variant?: TooltipVariantProps['variant'];
}

const TooltipComponent = (
  props: TooltipProps,
  ref: ComponentPropsWithRef<'div'>['ref'],
) => {
  const tooltipRecipe = useSlotRecipe({ key: 'tooltip' });
  const [variantProps, localProps] = tooltipRecipe.splitVariantProps(props);
  const styles = tooltipRecipe(variantProps);
  const {
    showArrow,
    children,
    disabled,
    portalled = true,
    content,
    contentProps,
    portalRef,
    ...rest
  } = localProps;

  if (disabled) return children;

  return (
    <ChakraTooltip.Root {...rest}>
      <ChakraTooltip.Trigger asChild>{children}</ChakraTooltip.Trigger>
      <Portal container={portalRef} disabled={!portalled}>
        <ChakraTooltip.Positioner>
          <ChakraTooltip.Content
            ref={ref}
            {...contentProps}
            css={styles.content}
          >
            {showArrow && (
              <ChakraTooltip.Arrow css={styles.arrow}>
                <ChakraTooltip.ArrowTip css={styles.arrowTip} />
              </ChakraTooltip.Arrow>
            )}
            {content}
          </ChakraTooltip.Content>
        </ChakraTooltip.Positioner>
      </Portal>
    </ChakraTooltip.Root>
  );
};

export const Tooltip = memo(
  forwardRef<HTMLDivElement, TooltipProps>(TooltipComponent),
);
